<template>
  <div class="bg-white-1 flex flex-col rounded p-5">
    <div class="flex items-center justify-between">
      <button
        class="flex items-center gap-2"
        :aria-expanded="opened"
        :aria-controls="panelId"
        @click="opened = !opened"
      >
        <Transition mode="out-in">
          <RfBaseSkeleton
            v-if="skeleton"
            height="29"
            width="122"
            :srLabel="props.title && `Loading ${props.title}`"
          />
          <h2 v-else class="text-heading-1 text-beige-90 text-start">
            {{ props.title }}
          </h2>
        </Transition>
        <Transition mode="out-in">
          <RfChevronMiniClosedIcon v-if="!opened" class="my-auto h-7! w-7!" />
          <RfChevronMiniOpenIcon v-else class="my-auto h-7! w-7!" />
        </Transition>
      </button>
      <slot name="actions"></slot>
    </div>
    <div
      :id="panelId"
      class="grid-rows-0fr transition-grid-template-rows grid"
      :class="{ 'grid-rows-1fr': opened }"
    >
      <div class="transition-padding overflow-hidden duration-500" :class="{ 'pt-3': opened }">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, ref } from "vue";
import RfChevronMiniClosedIcon from "@/components/icons/RfChevronMiniClosedIcon.vue";
import RfChevronMiniOpenIcon from "@/components/icons/RfChevronMiniOpenIcon.vue";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";

const props = defineProps({
  title: String,
  initOpenTrue: { type: Boolean, default: false },
  skeleton: { type: Boolean, default: false },
});

const { uid } = getCurrentInstance();
const panelId = `rf-accordion-panel-${uid}`;

// A caller that never binds v-model:opened at all falls back to internal state seeded from
// initOpenTrue; one that does drives it entirely through the model. Read/write dispatch on
// openedModel's current value rather than resolving this once, so a caller that starts uncontrolled
// and only later binds v-model:opened (or vice versa) keeps working instead of getting stuck on
// whichever mode was live at setup.
const openedModel = defineModel("opened", { type: Boolean, default: undefined });
const internalOpened = ref(props.initOpenTrue);
const opened = computed({
  get: () => openedModel.value ?? internalOpened.value,
  set: value => {
    if (openedModel.value !== undefined) openedModel.value = value;
    else internalOpened.value = value;
  },
});
</script>
